import api from '@/redux/api/catalog';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const FETCH_CATALOG_CURATORS_REQUEST = 'FETCH_CATALOG_CURATORS_REQUEST';
export const FETCH_CATALOG_CURATORS_SUCCESS = 'FETCH_CATALOG_CURATORS_SUCCESS';
export const FETCH_CATALOG_CURATORS_FAIL = 'FETCH_CATALOG_CURATORS_FAIL';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;
    let catalogId;

    switch (action.type) {
        case FETCH_CATALOG_CURATORS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case FETCH_CATALOG_CURATORS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case FETCH_CATALOG_CURATORS_SUCCESS:
            catalogId = action.meta.catalogId;
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            existing[catalogId] = action.payload;
            loaded[catalogId] = time;
            loading = difference(loading, [catalogId]);
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogCurators;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const isLoading: (any, number) => boolean = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);

export const getCatalogCurators: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || []
);

const getLoadTimeForCatalogCurators = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const shouldFetchCurators = (state, catalogId) => {
    if (!catalogId) {
        return false;
    }
    const curators = getCatalogCurators(state, catalogId);
    if (!isEmpty(curators)) {
        const loaded = getLoadTimeForCatalogCurators(state, catalogId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    } else if (getLoadTimeForCatalogCurators(state, catalogId)) {
        return false;
    }
    const loading = isLoading(state, catalogId);
    return !loading;
};

/* ACTION CREATORS */
export const fetchCatalogCuratorsIfNeeded = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchCurators(state, catalogId)) {
        const deployment = getDeployment(state);
        try {
            dispatch({
                meta: { actionTime: Date.now() },
                payload: catalogId,
                type: FETCH_CATALOG_CURATORS_REQUEST,
            });
            const response = await api.fetchCuratorsForCatalog({
                catalogId,
                deployment,
            });
            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.data,
                type: FETCH_CATALOG_CURATORS_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: FETCH_CATALOG_CURATORS_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};
